import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TableContainer from "Common/Tables/StringsFiltersTable/TableContainer";
import ActionButton from "components/Buttons/ActionButton";
import StatusChip from "components/Chips/StatusChip";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import {
  DynamicMenuItem,
  MenuItemConfig,
} from "components/Menuitem/DynamicMenuItem";
import ListingPageContainer from "components/PageContainer/ListingPageContainer";
import SearchBox from "components/SearchBox/SearchBox";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";
import useClearStateOnNavigation from "CustomHooks/useClearStateOnNavigation";
import usePermission from "CustomHooks/usePermission";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { app_modules } from "helpers/apps_modules_list";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import { exportSiteVisitCSV, fetchSiteVisit } from "slices/siteVisit/thunk";
import Swal from "sweetalert2";
import { showCustomSwal } from "utils/swalHelper";

const Sitevisit = () => {
  document.title = "Site visit List";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const location = useLocation();

  const selectSiteVisits = createSelector(
    (state: any) => state.siteVisitReducer,
    (data) => ({
      list: data.list || data?.enquiryList,
      rowCount: data.rowCount,
      loading: data.loading,
      error: data.error,
    })
  );

  const { list, rowCount, loading, error } = useSelector(selectSiteVisits);

  // Use custom hook to fetch and access permissions
  const { canView, canUpdate } = usePermission(
    app_modules?.order?.app_name,
    app_modules?.order?.modules?.site_visit
  );

  // 🔥 Use preserved table state hook
  const tableState = usePreservedTableState({
    key: "siteVisit",
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  const [data, setData] = useState([]);

  // Clear state when navigating from create action (not edit)
  useClearStateOnNavigation(location, resetState);

  useEffect(() => {
    dispatch(
      fetchSiteVisit({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageIndex, pageSize, sorting, search, filterValue]);


  useEffect(() => {
    setData(list);
  }, [list]);

  // ✅ Debounce search input to actual search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const trimmedInput = searchInput.trim();

      const dateMatch = trimmedInput.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

      if (dateMatch) {
        const [, day, month, year] = dateMatch;
        const isoDate = `${year}-${month}-${day}`;
        setSearch(isoDate);
      } else {
        setSearch(trimmedInput);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput, setSearch]);

  const handleExport = async () => {
    // Show loading modal
    showCustomSwal({
      title: "Exporting...",
      text: "Please wait while we export your data.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const response = await exportSiteVisitCSV({
        search,
        filters: filterValue,
        columnFilters,
        sort: sortField,
        order: sortDirection,
        page: pageIndex + 1,
        pageSize,
      });

      // response.data is Blob
      const blob = response.data as Blob;

      // parse filename from response headers if available
      const contentDisposition =
        response.headers["content-disposition"] ||
        response.headers["Content-Disposition"] ||
        "";
      let filename = `site_visit_${moment().format("YYYY-MM-DD")}.csv`;
      const match = /filename="?(.+)"?/.exec(contentDisposition);
      if (match && match[1]) filename = match[1];

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // Replace loading modal with success modal
      showCustomSwal({
        icon: "success",
        title: "Export Completed",
        text: "Enquiries exported successfully.",
      });
    } catch (error: any) {
      // Replace loading modal with error modal
      showCustomSwal({
        icon: "error",
        title: "Export Failed",
        text: error?.response?.data?.message || error?.message || "Failed to export CSV.",
      });
      console.error("Export failed", error);
    }
  };

  // Create menu items for DynamicMenuItem
  const menuItems: MenuItemConfig[] = useMemo(() => {
    const items: MenuItemConfig[] = [
      {
        icon: EditIcon,
        label: "Edit",
        permission: canUpdate,
        onClick: (siteVisit: any) => {
          navigate(`/sitevisit-form/${siteVisit.enquiry_id}`);
        },
        color: "themeColor",
      },
      {
        icon: VisibilityIcon,
        label: "Details",
        permission: canView,
        onClick: (siteVisit: any) => {
          navigate(`/sitevisit-details/${siteVisit.enquiry_id}`);
        },
        color: "themeColor",
      },
    ];
    return items;
  }, [canUpdate, canView, navigate]);

  const DynamicAction = useMemo(() => {
    // Only show Action column if user has at least one permission
    const hasPermission = canUpdate || canView;

    if (!hasPermission) return [];

    // Also check menuItems as a fallback
    const hasAnyMenuItemPermission = menuItems.some(
      (item) => item.permission === true
    );

    if (!hasAnyMenuItemPermission) return [];

    return [
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const stage = row.original?.stage?.toLowerCase()
          if(stage === "closed" || stage === "request for quotation" || stage === "quotation created"){
             const filteredMenuItems = menuItems.filter(item=> item.label === "Details" )
            return ( <DynamicMenuItem row={row.original} menuItems={filteredMenuItems} /> )
          }else {
            return ( <DynamicMenuItem row={row.original} menuItems={menuItems} /> )
          }
        },
      },
    ];
  }, [canUpdate, canView, menuItems]);

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    try {
      return moment(dateString).format("DD-MM-YY");
    } catch {
      return dateString;
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "Sr No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row, index }: { row: any; index: number }) =>
          row.original.sr_no || index + 1 + pageIndex * pageSize || "-",
      },
      {
        header: "Enquiry No.",
        accessorKey: "enquiry_number",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/site-visit/dropdown/enquiry_number",
        cell: ({ row }: { row: any }) => row.original.enquiry_number || "-",
      },
      {
        header: "Enquiry Date",
        accessorKey: "enquiry_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          return new Date(row.original.enquiry_date).toISOString().split("T")[0];;
        },
      },
      {
        header: "Customer Name",
        accessorFn: (row: any) => row.customer_details?.customer_name || "",
        id: "customer_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/site-visit/dropdown/customer_name",
        cell: ({ row }: { row: any }) => {
          return row.original.customer_details?.customer_name || "-";
        },
      },
      {
        header: "Phone Number",
        accessorKey: "contact_person_phone_no",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          return row.original.customer_details?.contact_person_phone_no || "-";
        },
      },
      {
        header: "Site Visit",
        accessorKey: "site_visit_required",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          return row.original.site_visit_required ? "Yes" : "No";
        },
      },
      {
        header: "Stage",
        accessorKey: "stage",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/enquiry/dropdown/stage",
        cell: ({ row }: { row: any }) => {
          // return  row.original.stage || "-"
          return (
          //  <div className="text-center">
             <StatusChip status={row.original.stage}>
              {row.original.stage}
            </StatusChip>
          //  </div>
          );
        },
      },
      {
        header: "Staff Name",
        accessorKey: "staff_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/site-visit/dropdown/staff_name",
        cell: ({ row }: { row: any }) => {
          // Get staff name from site_visit object (denormalized field)
          // staff_details is an object, but staff_name is the denormalized string field
          return row.original.site_visit?.staff_name || "-";
        },
      },
      {
        header: "Source",
        accessorKey: "enquiry_source",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/site-visit/dropdown/source_of_enquiry",
        cell: ({ row }: { row: any }) => {
          return row.original.enquiry_source || "-";
        },
      },
      {
        header: "Site Visit Date",
        accessorKey: "site_visit_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          return row.original.site_visit?.site_visit_date
            ? new Date(row.original.site_visit.site_visit_date).toISOString().split("T")[0]
            : "-";
        },
      },
      {
        header: "Remark",
        accessorKey: "remark",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <TruncatedTextCell
            text={row?.original?.remark}
            maxWidth="300px"
            maxLines={4}
            rowIndex={row.index}
            tooltipId={`remark-tooltip-${row.index}`}
          />
        ),
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name =
            row.original.created_by_user?.username ||
            row.original.created_by_user?.first_name ||
            "N/A";
          const date = row.original.created_at
            ? formatDate(row.original.created_at)
            : "N/A";
          return `${name} - ${date}`;
        },
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name =
            row.original.updated_by_user?.username ||
            row.original.updated_by_user?.first_name ||
            "N/A";
          const date = row.original.updated_at
            ? formatDate(row.original.updated_at)
            : "N/A";
          return `${name} - ${date}`;
        },
      },
      ...DynamicAction,
      //   {
      //     header: "Action",
      //     enableColumnFilter: false,
      //     enableSorting: false,
      //     cell: ({ row }: { row: any }) => {
      //       const data = row.original;
      //       const conditionalArray = [];
      //       // if (warehouse_permissions?.permissions?.Update === true) {
      //       conditionalArray.push({
      //         label: "Edit",
      //         icon: <EditOutlinedIcon fontSize="small" />,
      //         onClick: () =>
      //           navigate(`/sitevisit-form/${row?.original?.enquiry_id}`, {
      //             state: data,
      //           }),
      //       });
      //       // }
      //       // if (warehouse_permissions?.permissions?.Update === true) {
      //       conditionalArray.push({
      //         label: "View",
      //         icon: <VisibilityOutlinedIcon fontSize="small" />,
      //         onClick: () =>
      //           navigate(`/sitevisit-details/${row?.original?.enquiry_id}`, {
      //             state: data,
      //           }),
      //       });
      //       // }

      //       return <TemplateActionMenu menuItemsOptions={conditionalArray} />;
      //     },
      //   },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <ListingPageContainer>
      <style>
        {`
          .truncated-text-cell-wrapper {
            min-width: 0 !important;
            display: -webkit-box !important;
            -webkit-box-orient: vertical !important;
            overflow: hidden !important;
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
            white-space: normal !important;
            line-height: 1.5em !important;
          }
        `}
      </style>
      <Row id="invoiceList">
        <Col lg={12}>
          <Card>
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col lg={3} className="me-auto">
                  <h6 className="card-title mb-0">Site Visit</h6>
                </Col>

                {/* Search Input Box (reusable)*/}
                <Col xl={2} md={3}>
                  <SearchBox value={searchInput} onChange={setSearchInput} />
                </Col>

                <Col className="col-md-auto">
                  {canView && (
                    <ActionButton
                      label="Export"
                      icon="bi bi-box-arrow-up"
                      handleClick={handleExport}
                      variant="light"
                    />
                  )}
                </Col>

                {/* <Col className="col-md-auto"></Col> */}
              </Row>
            </Card.Header>
            <Card.Body className="mt-3">
              <LoaderErrorWrapper loading={loading} error={error}>
                <TableContainer
                  columns={columns}
                  data={data}
                  totalRecords={rowCount}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  colValue={setFilterValue}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  isBordered={false}
                  isPagination={true}
                  isGlobalFilter={false}
                  SearchPlaceholder="Search ..."
                  manualSorting={true}
                />
              </LoaderErrorWrapper>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ListingPageContainer>
  );
};

export default Sitevisit;
